import React, { useEffect, useMemo, useState } from 'react';
import { StatCard, EmptyState } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Wallet, PlusCircle, Trash2, IndianRupee, TrendingDown, Users, Pencil, Sliders, ChevronRight } from 'lucide-react';
import { fmtINR, monthName } from '@/lib/format';

function LoanDialog({ companyId, employees, existing, onSaved }) {
  const [open, setOpen] = useState(false);
  const nowMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const initial = () => ({
    employee_id: existing?.employee_id || '',
    principal: existing?.principal ?? '',
    tenure_months: existing?.tenure_months ?? 12,
    monthly_emi: existing?.monthly_emi ?? '',
    start_month: existing?.start_month || nowMonth(),
    notes: existing?.notes || '',
  });
  const [f, setF] = useState(initial());
  useEffect(() => { if (open) setF(initial()); }, [open, existing?.id]);

  const computedEmi = useMemo(() => {
    if (f.monthly_emi && +f.monthly_emi > 0) return +f.monthly_emi;
    if (f.principal && f.tenure_months && +f.tenure_months > 0) return Math.round((+f.principal / +f.tenure_months) * 100) / 100;
    return 0;
  }, [f.principal, f.tenure_months, f.monthly_emi]);

  const submit = async () => {
    if (!f.employee_id) return toast.error('Pick an employee');
    if (!f.principal || +f.principal <= 0) return toast.error('Principal must be greater than 0');
    if (!f.tenure_months || +f.tenure_months <= 0) return toast.error('Tenure must be at least 1 month');
    if (!/^\d{4}-\d{2}$/.test(f.start_month)) return toast.error('Start month must be YYYY-MM');
    const payload = {
      employee_id: f.employee_id,
      principal: +f.principal,
      tenure_months: +f.tenure_months,
      monthly_emi: f.monthly_emi ? +f.monthly_emi : null,
      start_month: f.start_month,
      notes: f.notes || null,
    };
    try {
      if (existing) {
        await api.patch(`/loans/${existing.id}`, payload);
      } else {
        await api.post('/loans', { ...payload, company_id: companyId });
      }
      toast.success(existing ? 'Loan updated' : 'Loan added');
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {existing ? (
          <Button size="icon" variant="ghost" data-testid={`loan-edit-${existing.id}`} title="Edit loan"><Pencil className="h-4 w-4 text-muted-foreground" /></Button>
        ) : (
          <Button data-testid="loan-add-button"><PlusCircle className="h-4 w-4 mr-2" />New loan / advance</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit loan / advance' : 'New loan / advance'}</DialogTitle>
          <DialogDescription>Enter the loan amount and tenure. The monthly EMI is auto-calculated but you can override it. Future EMIs will regenerate if you change any of principal / tenure / EMI. Already-deducted months are never modified.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label>Employee</Label>
            <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })} disabled={!!existing}>
              <SelectTrigger data-testid="loan-employee"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code || '—'}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Principal (₹)</Label><Input type="number" value={f.principal} onChange={(e) => setF({ ...f, principal: e.target.value })} data-testid="loan-principal" /></div>
            <div className="space-y-1.5"><Label>Tenure (months)</Label><Input type="number" min="1" value={f.tenure_months} onChange={(e) => setF({ ...f, tenure_months: e.target.value })} data-testid="loan-tenure" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Monthly EMI (₹)</Label><Input type="number" value={f.monthly_emi} onChange={(e) => setF({ ...f, monthly_emi: e.target.value })} placeholder={`Auto: ${fmtINR(computedEmi)}`} data-testid="loan-emi" /></div>
            <div className="space-y-1.5"><Label>First EMI month</Label><Input type="month" value={f.start_month} onChange={(e) => setF({ ...f, start_month: e.target.value })} data-testid="loan-start-month" /></div>
          </div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="e.g., Advance for medical emergency" /></div>
          <div className="rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
            EMI to be deducted every month: <span className="font-semibold text-foreground">{fmtINR(computedEmi)}</span>
            {' · '}Total repayable: <span className="font-medium text-foreground">{fmtINR(computedEmi * (+f.tenure_months || 0))}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="loan-save">{existing ? 'Save changes' : 'Create loan'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverrideMonthDialog({ loan, onSaved }) {
  const [open, setOpen] = useState(false);
  const pendingRows = useMemo(() => (loan.schedule || []).filter((r) => !r.deducted), [loan]);
  const [month, setMonth] = useState(pendingRows[0]?.month || '');
  const [emi, setEmi] = useState(pendingRows[0]?.emi_amount ?? loan.monthly_emi);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      const first = pendingRows[0];
      setMonth(first?.month || '');
      setEmi(first?.emi_amount ?? loan.monthly_emi);
      setNote('');
    }
  }, [open, loan.id]);

  const submit = async () => {
    if (!month) return toast.error('Pick a month to override');
    if (emi === '' || +emi < 0) return toast.error('EMI amount is required');
    try {
      await api.post(`/loans/${loan.id}/schedule-override`, { month, emi_amount: +emi, note: note || null });
      toast.success(`EMI for ${month} updated. Future months rebalanced automatically.`);
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" data-testid={`loan-override-${loan.id}`} title="Change EMI for a specific month">
          <Sliders className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change EMI for a specific month · {loan.employee_name}</DialogTitle>
          <DialogDescription>Pick a pending month and enter the new EMI amount for that month only. Remaining months are automatically re-balanced so the total repayment still matches the principal.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label>Month to change</Label>
            <Select value={month} onValueChange={(v) => {
              setMonth(v);
              const r = (loan.schedule || []).find((x) => x.month === v);
              if (r) setEmi(r.emi_amount);
            }}>
              <SelectTrigger data-testid="override-month"><SelectValue placeholder="Pick month" /></SelectTrigger>
              <SelectContent>
                {pendingRows.map((r) => (
                  <SelectItem key={r.month} value={r.month}>{r.month} · currently {fmtINR(r.emi_amount)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>New EMI amount (₹)</Label>
            <Input type="number" value={emi} onChange={(e) => setEmi(e.target.value)} data-testid="override-emi" />
          </div>
          <div className="space-y-1.5">
            <Label>Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g., Reduced due to festival advance request" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="override-save">Save month</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoanScheduleDetails({ loan }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" title="View schedule" data-testid={`loan-schedule-${loan.id}`}>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Repayment schedule · {loan.employee_name}</DialogTitle>
          <DialogDescription>Deducted months are locked; future months can be overridden individually.</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto rounded border">
          <Table>
            <TableHeader><TableRow><TableHead>Month</TableHead><TableHead className="text-right">EMI</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {(loan.schedule || []).map((r) => (
                <TableRow key={r.month}>
                  <TableCell className="text-sm">{r.month}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtINR(r.emi_amount)}</TableCell>
                  <TableCell>
                    {r.deducted ? (
                      <Badge className="bg-[hsl(var(--success-soft))] text-[hsl(var(--success))] border-transparent text-[11px]">Deducted</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[11px]">Pending</Badge>
                    )}
                    {r.override_note && <p className="text-[10px] text-muted-foreground mt-1">{r.override_note}</p>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function LoansTab({ companyId, employees }) {
  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get(`/loans?company_id=${companyId}${filterStatus !== 'all' ? `&status=${filterStatus}` : ''}`),
        api.get(`/loans/summary?company_id=${companyId}`),
      ]);
      setLoans(l.data); setSummary(s.data);
    } catch (e) { /* eslint-disable-line no-empty */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [companyId, filterStatus]);

  const del = async (loan) => {
    if (!window.confirm(`Delete loan for ${loan.employee_name}? Deducted months are preserved in payroll history but the loan record will be removed.`)) return;
    try { await api.delete(`/loans/${loan.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total loan burden" value={fmtINR(summary?.total_remaining || 0)} icon={IndianRupee} tone="primary" testId="stat-loan-burden" />
        <StatCard label="Total loan principal" value={fmtINR(summary?.total_principal || 0)} icon={Wallet} />
        <StatCard label="Already deducted" value={fmtINR(summary?.total_deducted || 0)} icon={TrendingDown} tone="good" />
        <StatCard label="Employees with loan" value={summary?.employees_with_loan || 0} icon={Users} hint={`${summary?.active_loans || 0} active loans`} />
      </div>

      <div className="flex items-center gap-2">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 h-9" data-testid="loan-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paid">Paid off</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <LoanDialog companyId={companyId} employees={employees} onSaved={load} />
        </div>
      </div>

      {loans.length === 0 ? (
        <EmptyState icon={Wallet} title="No loans yet" description="Add the first loan or salary advance for an employee. Deductions will apply automatically at each monthly payroll run." action={<LoanDialog companyId={companyId} employees={employees} onSaved={load} />} />
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Employee</TableHead>
              <TableHead className="text-right">Principal</TableHead>
              <TableHead className="text-right">Monthly EMI</TableHead>
              <TableHead className="text-right">Tenure</TableHead>
              <TableHead className="text-right">Deducted</TableHead>
              <TableHead className="text-right">Remaining</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loans.map((l) => {
                const pct = l.totals?.principal > 0 ? Math.round((l.totals.deducted / l.totals.principal) * 100) : 0;
                return (
                  <TableRow key={l.id} data-testid={`loan-row-${l.id}`}>
                    <TableCell>
                      <p className="font-medium">{l.employee_name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{l.employee_code || l.employee_email || ''}</p>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtINR(l.principal)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtINR(l.monthly_emi)}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{l.tenure_months}m</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtINR(l.totals?.deducted || 0)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{fmtINR(l.totals?.remaining || 0)}</TableCell>
                    <TableCell className="min-w-[120px]">
                      <div className="space-y-1">
                        <Progress value={pct} className="h-2" />
                        <p className="text-[10px] text-muted-foreground">{pct}% paid · from {l.start_month}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={l.status === 'paid' ? 'secondary' : 'outline'} className={l.status === 'paid' ? 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]' : ''}>{l.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-0.5">
                        <LoanScheduleDetails loan={l} />
                        <OverrideMonthDialog loan={l} onSaved={load} />
                        <LoanDialog companyId={companyId} employees={employees} existing={l} onSaved={load} />
                        <Button size="icon" variant="ghost" onClick={() => del(l)} data-testid={`loan-delete-${l.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Loans get deducted automatically during payroll for the months they are scheduled for. You can override any pending month above,
        or edit the deduction inline in the payroll run editor. Once a run is finalized, the corresponding month is marked deducted and locked.
      </p>
    </div>
  );
}
